<template>
  <div :class="$style.listContainer">
    <CalendarSummaryController
      v-model:search-condition="searchCondition"
      v-model:sort-option="sortOption"
    />
    <ul :class="$style.list">
      <li
        v-for="calendarSummary in currentPageItems"
        :key="calendarSummary.guid"
        :class="$style.list__item"
      >
        <CalendarSummaryItem
          :calendar-summary="calendarSummary"
          :linked-staff-guids="linkedStaffGUIDs"
        />
      </li>
    </ul>
    <CalendarSummaryPaginator
      v-model="currentPage"
      :class="$style.listContainer__paginator"
      :number-of-pages="numberOfPages"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import CalendarSummaryItem from './CalendarSummaryItem.vue'
import CalendarSummaryPaginator from './CalendarSummaryPaginator.vue'

import type { CalendarSummaryViewCalendarSummaryFragment } from './CalendarSummaryView.generated'
import { searchCalendarSummary, type SearchCondition } from './search'
import CalendarSummaryController from './CalendarSummaryController.vue'
import { sortCalendarSummary, type SortOption } from './sort'

const { calendarSummaries } = defineProps<{
  calendarSummaries: CalendarSummaryViewCalendarSummaryFragment[]
  linkedStaffGUIDs: string[]
}>()

const searchCondition = ref({} as SearchCondition)
const sortOption = ref('createdAtDesc' as SortOption)

const sortAndFilteredCalendarSummaries = computed(() => {
  const filteredCalendarSummaries = searchCalendarSummary(
    searchCondition.value,
    calendarSummaries
  )
  return sortCalendarSummary(sortOption.value, filteredCalendarSummaries)
})

const currentPage = ref(1)
const SIZE_PER_PAGE = 5

const currentPageItems = computed(() => {
  const start = (currentPage.value - 1) * SIZE_PER_PAGE
  const end = start + SIZE_PER_PAGE
  return sortAndFilteredCalendarSummaries.value.slice(start, end)
})

const numberOfPages = computed(() => {
  return Math.ceil(
    sortAndFilteredCalendarSummaries.value.length / SIZE_PER_PAGE
  )
})

watch(numberOfPages, numberOfPagesNewValue => {
  if (currentPage.value > numberOfPagesNewValue) {
    currentPage.value = Math.max(numberOfPagesNewValue, 1)
  }
})
</script>

<style lang="scss" module>
.listContainer {
  display: flex;
  flex-direction: column;
  gap: 8px;

  &__paginator {
    align-self: center;
  }
}

.list {
  display: flex;
  flex-direction: column;
  gap: 4px;

  &__item {
    padding: 8px;
    border: 1px solid $color-border-low-emphasis;
    border-radius: $border-radius-v2-base;

    &:hover {
      background-color: $color-surface-accent-primary-light;
    }
  }
}
</style>
